import React, { useState, useEffect, useMemo } from 'react';
import NoteInput from './NoteInput';
import NoteCard from './NoteCard';
import useMeetingNotes from '../../hooks/useMeetingNotes';

// Inline SVG icons
const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const DocumentTextIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const CalendarIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const QuestionMarkIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Hjälp-tooltip komponent för röstkommandon
const VoiceCommandsHelp = () => (
  <div className="relative group inline-block">
    <button
      type="button"
      className="text-gray-400 hover:text-gray-600 focus:outline-none"
      title="Röstkommandon"
    >
      <QuestionMarkIcon className="w-5 h-5" />
    </button>
    <div className="absolute right-0 top-7 z-50 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
      <div className="font-semibold mb-2">Röstkommandon:</div>
      <table className="w-full">
        <tbody>
          <tr><td className="pr-2 py-0.5 text-gray-300">"stopp"</td><td>→ avslutar inspelningen</td></tr>
          <tr><td className="pr-2 py-0.5 text-gray-300">"punkt"</td><td>→ .</td></tr>
          <tr><td className="pr-2 py-0.5 text-gray-300">"komma"</td><td>→ ,</td></tr>
          <tr><td className="pr-2 py-0.5 text-gray-300">"bindestreck"</td><td>→ -</td></tr>
          <tr><td className="pr-2 py-0.5 text-gray-300">"radbryt"</td><td>→ ny rad</td></tr>
        </tbody>
      </table>
      {/* Pil */}
      <div className="absolute -top-1 right-3 w-2 h-2 bg-gray-900 transform rotate-45"></div>
    </div>
  </div>
);

/**
 * Modal för snabb anteckningsinmatning från översikten
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Om modalen är öppen
 * @param {function} props.onClose - Callback för att stänga
 * @param {Object} props.project - Projektobjekt med meetings
 * @param {string} props.preselectedMeetingId - Förvalt mötes-ID (optional)
 */
export function MeetingNoteModal({
  isOpen,
  onClose,
  project,
  preselectedMeetingId
}) {
  // Memoize meetings och scheduledMeetings för att undvika oändliga re-renders
  const meetings = useMemo(() => project?.meetings || [], [project?.meetings]);

  const scheduledMeetings = useMemo(() =>
    meetings.filter(m => m.status === 'scheduled' || m.status === 'completed'),
    [meetings]
  );

  // Initialt valt möte: preselected > enda mötet > tomt
  const initialMeetingId = useMemo(() => {
    if (preselectedMeetingId) return preselectedMeetingId;
    if (scheduledMeetings.length === 1) return scheduledMeetings[0]._id;
    return '';
  }, [preselectedMeetingId, scheduledMeetings]);

  const [selectedMeetingId, setSelectedMeetingId] = useState(initialMeetingId);

  // Uppdatera bara vid ändring av preselectedMeetingId eller när modalen öppnas med nytt projekt
  useEffect(() => {
    if (preselectedMeetingId) {
      setSelectedMeetingId(preselectedMeetingId);
    } else if (scheduledMeetings.length === 1) {
      setSelectedMeetingId(scheduledMeetings[0]._id);
    }
    // Notera: Vi återställer INTE till '' här - låt användarens val stå kvar
    //
    // Hela scheduledMeetings i stället för bara .length: den är memoiserad upp
    // till project?.meetings och alltså stabil. Med bara längden som beroende
    // uppdaterades valet inte när listan byttes ut mot ett ANNAT enda möte.
  }, [preselectedMeetingId, scheduledMeetings]);

  const {
    notes,
    counts,
    loading,
    createNote,
    updateNote,
    deleteNote,
    updateVisibility
  } = useMeetingNotes(selectedMeetingId);

  // Hämta valt möte
  const selectedMeeting = meetings.find(m => m._id === selectedMeetingId);

  // Hantera skapa anteckning
  const handleCreateNote = async (noteData) => {
    await createNote(noteData);
  };

  // Formatera mötestid
  const formatMeetingTime = (meeting) => {
    if (!meeting?.scheduledAt) return '';
    const date = new Date(meeting.scheduledAt);
    return date.toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mötesnamn
  const getMeetingName = (meeting) => {
    const typeLabels = {
      'projektgenomgang': 'Projektgenomgång',
      'efter_rivning': 'Möte efter rivning',
      'byggmote': 'Byggmöte',
      'slutsyn': 'Slutsyn',
      'brief': 'Brief',
      'intromote': 'Intromöte',
      'avstamning': 'Avstämningsmöte',
      'fargkonsultation': 'Färgsättningskonsultation'
    };
    const label = typeLabels[meeting?.type] || meeting?.type;
    if (meeting?.type === 'byggmote' && meeting?.meetingNumber) {
      return `${label} #${meeting.meetingNumber}`;
    }
    return label;
  };

  // Stäng vid escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Förhindra scroll på body
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-lg shadow-xl flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <DocumentTextIcon className="w-5 h-5 text-leadme-pink" />
            Mötesanteckning
          </h2>
          <div className="flex items-center gap-2">
            <VoiceCommandsHelp />
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Projektnamn */}
        <div className="px-4 py-2 bg-gray-50 border-b">
          <span className="text-sm font-medium text-gray-700">
            {project?.projectId ? `#${project.projectId}` : (project?.name || 'Projekt')}
          </span>
        </div>

        {/* Mötesväljare (om flera möten) */}
        {scheduledMeetings.length > 1 && (
          <div className="px-4 py-3 border-b">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Välj möte
            </label>
            <select
              value={selectedMeetingId}
              onChange={(e) => setSelectedMeetingId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
            >
              <option value="">Välj ett möte...</option>
              {scheduledMeetings.map(meeting => (
                <option key={meeting._id} value={meeting._id}>
                  {getMeetingName(meeting)} - {formatMeetingTime(meeting)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Valt möte info */}
        {selectedMeeting && (
          <div className="px-4 py-2 bg-blue-50 border-b flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-800">
              {getMeetingName(selectedMeeting)} - {formatMeetingTime(selectedMeeting)}
            </span>
            {counts.total > 0 && (
              <span className="ml-auto text-xs text-blue-600">
                {counts.total} anteckningar
              </span>
            )}
          </div>
        )}

        {/* Content */}
        {!selectedMeetingId ? (
          <div className="flex-1 flex items-center justify-center p-8 text-gray-500">
            <div className="text-center">
              <CalendarIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>Välj ett möte för att lägga till anteckningar</p>
            </div>
          </div>
        ) : scheduledMeetings.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-8 text-gray-500">
            <div className="text-center">
              <CalendarIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>Inga bokade möten finns för detta projekt</p>
            </div>
          </div>
        ) : (
          <>
            {/* Befintliga anteckningar (scrollbar) */}
            <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
              {loading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-leadme-pink border-t-transparent" />
                </div>
              ) : notes.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm">
                  Inga anteckningar ännu
                </div>
              ) : (
                <div className="space-y-2">
                  {notes.slice(0, 5).map(note => (
                    <NoteCard
                      key={note._id}
                      note={note}
                      onUpdate={updateNote}
                      onDelete={deleteNote}
                      onToggleVisibility={updateVisibility}
                      compact
                    />
                  ))}
                  {notes.length > 5 && (
                    <p className="text-center text-xs text-gray-500 py-2">
                      + {notes.length - 5} fler anteckningar
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t">
              <NoteInput
                onSubmit={handleCreateNote}
                disabled={loading}
                placeholder="Diktera eller skriv en anteckning..."
                autoFocus
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MeetingNoteModal;
